import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck, KeyRound, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useCompany, Brand } from "../context/CompanyContext";

/**
 * Two-step password recovery:
 * - /recuperar-password — user enters email, backend silently dispatches a
 *   reset link (always returns 200 to prevent account enumeration).
 * - /reset-password?token=… — user picks a new password using the link sent
 *   to their inbox. Token is single-use, 30-minute TTL.
 */

function PasswordShell({ eyebrow, title, children }) {
  const { company, logoDark } = useCompany();
  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative bg-[#062c27]">
        <img alt="" src="https://images.pexels.com/photos/6196239/pexels-photo-6196239.jpeg"
             className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#041f1b]/60 to-[#041f1b]" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="inline-block">
            <span className="bg-[#fdfbf7] rounded-sm px-3 py-2 inline-flex items-center">
              <img src={logoDark} alt={company.trade_name} className="h-16 w-auto" />
            </span>
          </Link>
          <div>
            <div className="eyebrow mb-4">Segurança da sua conta</div>
            <h1 className="font-display text-5xl leading-tight">Recuperação<br/>discreta.</h1>
            <p className="mt-6 text-sm text-[#a0b2af] max-w-sm">
              Enviamos um link seguro para o seu email. Válido por 30 minutos e de uso único.
            </p>
          </div>
        </div>
      </div>

      <div className="p-8 md:p-16 flex items-center">
        <div className="w-full max-w-md space-y-6">
          <Link to="/login" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37]"
                data-testid="back-to-login">
            <ArrowLeft size={12} /> Voltar ao login
          </Link>
          <div>
            <div className="eyebrow mb-2">{eyebrow}</div>
            <h2 className="font-display text-4xl">{title}</h2>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password/reset-request", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <PasswordShell eyebrow="Email enviado" title="Verifique a sua caixa">
        <div className="lux-card p-6 space-y-3" data-testid="forgot-password-success">
          <div className="flex items-center gap-3">
            <MailCheck size={22} className="text-[#10b981]" />
            <div className="font-display text-lg">Pedido registado</div>
          </div>
          <p className="text-sm text-[#a0b2af]">
            Se o email <span className="text-[#fdfbf7]">{email}</span> corresponder a uma
            conta <Brand short />, enviámos um link para redefinir a palavra-passe.
          </p>
          <p className="text-xs text-[#a0b2af]">
            Verifique também a pasta de spam. O link é válido por 30 minutos.
          </p>
          <div className="flex gap-3 pt-2">
            <Link to="/login" className="btn-gold text-xs px-5 py-2.5 uppercase tracking-widest"
                  data-testid="forgot-password-back">
              Voltar ao login
            </Link>
            <button type="button" onClick={() => { setSent(false); setEmail(""); }}
                    className="text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37]"
                    data-testid="forgot-password-resend">
              Enviar outro
            </button>
          </div>
        </div>
      </PasswordShell>
    );
  }

  return (
    <PasswordShell eyebrow="Recuperar acesso" title="Esqueci a palavra-passe">
      <form onSubmit={submit} className="space-y-6" data-testid="forgot-password-form">
        <p className="text-sm text-[#a0b2af]">
          Introduza o seu email. Enviamos-lhe um link para escolher uma nova palavra-passe.
        </p>
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="exemplo@luxstay.pt"
                 autoComplete="email" inputMode="email" autoCapitalize="none" autoCorrect="off"
                 className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 text-base outline-none focus:border-[#d4af37] placeholder:text-[#1a4640]"
                 data-testid="forgot-password-email" />
        </div>
        <button disabled={busy || !email} type="submit"
                className="btn-gold w-full py-4 text-base disabled:opacity-60"
                data-testid="forgot-password-submit">
          {busy ? "A enviar…" : "Enviar link de recuperação"}
        </button>
      </form>
    </PasswordShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function submit(e) {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("A palavra-passe deve ter pelo menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("As palavras-passe não coincidem.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/password/reset-confirm", { token, new_password: password });
      toast.success("Palavra-passe atualizada. Pode fazer login.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <PasswordShell eyebrow="Link inválido" title="Link incompleto">
        <div className="lux-card p-6 space-y-3">
          <p className="text-sm text-[#a0b2af]">
            O link de recuperação está incompleto ou inválido. Peça um novo email.
          </p>
          <Link to="/recuperar-password" className="btn-gold text-xs px-5 py-2.5 uppercase tracking-widest inline-block"
                data-testid="reset-no-token-link">
            Pedir novo email
          </Link>
        </div>
      </PasswordShell>
    );
  }

  return (
    <PasswordShell eyebrow="Nova palavra-passe" title="Escolher nova palavra-passe">
      <form onSubmit={submit} className="space-y-6" data-testid="reset-password-form">
        <div className="flex items-center gap-2 text-sm text-[#a0b2af]">
          <KeyRound size={14} className="text-[#d4af37]" />
          Defina a sua nova palavra-passe abaixo.
        </div>
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Nova palavra-passe</label>
          <input type="password" required minLength={6} value={password}
                 onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
                 className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 text-base outline-none focus:border-[#d4af37]"
                 data-testid="reset-password-new" />
          <div className="text-[11px] text-[#a0b2af]">Mínimo 6 caracteres.</div>
        </div>
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Confirmar palavra-passe</label>
          <input type="password" required minLength={6} value={confirm}
                 onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password"
                 className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 text-base outline-none focus:border-[#d4af37]"
                 data-testid="reset-password-confirm" />
        </div>
        <button disabled={busy || !password || !confirm} type="submit"
                className="btn-gold w-full py-4 text-base disabled:opacity-60"
                data-testid="reset-password-submit">
          {busy ? "A guardar…" : "Atualizar palavra-passe"}
        </button>
      </form>
    </PasswordShell>
  );
}

export default ForgotPassword;
